/**
 * Excel Viewer Component
 * Parse and display actual Excel file data using XLSX library
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import { GridOn as ExcelIcon, Fullscreen, FullscreenExit, Search as SearchIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useBOM } from '../contexts/BOMContext';

interface ExcelViewerProps {
  xlsUrl: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

const ExcelViewer: React.FC<ExcelViewerProps> = ({ xlsUrl, isExpanded = false, onToggleExpand }) => {
  const { setExcelGroundTruth } = useBOM();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [worksheetData, setWorksheetData] = useState<any[][]>([]);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [currentSheet, setCurrentSheet] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter worksheet data based on search query
  const filteredWorksheetData = useMemo(() => {
    if (!searchQuery.trim() || worksheetData.length === 0) {
      return worksheetData.slice(1); // Skip header row
    }
    
    const query = searchQuery.toLowerCase();
    const headers = worksheetData[0] || [];
    
    return worksheetData.slice(1).filter((row) => {
      // Search across all cells in the row
      const searchableText = row.map((cell: any) => 
        cell !== null && cell !== undefined ? String(cell).toLowerCase() : ''
      ).join(' ');
      
      return searchableText.includes(query);
    });
  }, [worksheetData, searchQuery]);

  useEffect(() => {
    const loadExcel = async () => {
      try {
        setLoading(true);
        setError(null);

        // Construct the full URL if it's relative
        const fullXlsUrl = xlsUrl.startsWith('http') 
          ? xlsUrl 
          : `${window.location.origin}${xlsUrl}`;

        console.log('[ExcelViewer] Loading Excel from:', fullXlsUrl);

        const response = await fetch(fullXlsUrl);
        if (!response.ok) {
          throw new Error(`Failed to load Excel file: ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        
        // Parse the Excel file using XLSX
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        console.log('[ExcelViewer] Workbook loaded:', workbook.SheetNames);
        
        setSheetNames(workbook.SheetNames);
        
        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        setCurrentSheet(firstSheetName);
        
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to array of arrays (including headers)
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
        
        console.log('[ExcelViewer] Parsed data rows:', jsonData.length);
        
        setWorksheetData(jsonData as any[][]);
        
        // Parse and store ground truth data for comparison
        if (jsonData.length > 1) {
          const headers = (jsonData[0] as any[]).map((h: any) => String(h || '').toLowerCase().trim());
          const groundTruthData: any[] = [];
          
          // Find description and qty column indices
          const descColIdx = headers.findIndex((h: string) => 
            h.includes('description') || h.includes('item') || h.includes('part') || h.includes('device') || h.includes('component')
          );
          const qtyColIdx = headers.findIndex((h: string) => 
            h.includes('qty') || h.includes('quantity') || h.includes('qty.')
          );
          
          // Parse rows into objects
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i] as any[];
            if (row && row.length > 0) {
              const rowObj: any = {};
              headers.forEach((header, idx) => {
                rowObj[header] = row[idx] !== null && row[idx] !== undefined ? String(row[idx]) : '';
              });
              // Store normalized description and qty for easy matching
              rowObj._description = descColIdx >= 0 ? String(row[descColIdx] || '').toLowerCase().trim() : '';
              rowObj._qty = qtyColIdx >= 0 ? (row[qtyColIdx] || '') : '';
              groundTruthData.push(rowObj);
            }
          }
          
          console.log('[ExcelViewer] Parsed ground truth:', groundTruthData.length, 'rows');
          setExcelGroundTruth(groundTruthData);
        } else {
          setExcelGroundTruth([]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('[ExcelViewer] Error loading Excel:', err);
        setError(err instanceof Error ? err.message : 'Failed to load Excel file');
        setLoading(false);
      }
    };

    loadExcel();
  }, [xlsUrl]);

  return (
    <Paper sx={{ mt: 2, overflow: 'hidden' }}>
      <Box 
        sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'background.default'
        }}
      >
        <ExcelIcon color="success" />
        <Typography variant="h6">Excel Preview (Ground Truth)</Typography>
        {loading && <CircularProgress size={20} sx={{ ml: 'auto' }} />}
        
        {/* Expand/Collapse Button */}
        {onToggleExpand && !loading && (
          <IconButton 
            size="small" 
            onClick={onToggleExpand}
            sx={{ ml: loading ? 0 : 'auto' }}
            title={isExpanded ? 'Show side-by-side' : 'Expand to full width'}
          >
            {isExpanded ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        )}
        
        {!loading && sheetNames.length > 0 && (
          <Box sx={{ ml: onToggleExpand ? 1 : 'auto', display: 'flex', gap: 1 }}>
            {sheetNames.map((name) => (
              <Chip
                key={name}
                label={name}
                color={name === currentSheet ? 'primary' : 'default'}
                size="small"
              />
            ))}
          </Box>
        )}
      </Box>

      {error && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading && !error && (
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading and parsing Excel file...
          </Typography>
        </Box>
      )}

      {!loading && !error && worksheetData.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 600, maxHeight: 600 }}>
          {/* Search field */}
          <Box sx={{ m: 2, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by any column..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          <TableContainer sx={{ flexGrow: 1, border: 1, borderColor: 'divider', overflow: 'auto', m: 2, mt: 0 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {worksheetData[0]?.map((header, idx) => (
                    <TableCell 
                      key={idx} 
                      sx={{ 
                        fontWeight: 700, 
                        backgroundColor: 'success.main', 
                        color: 'white',
                        minWidth: 100,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {header || `Column ${idx + 1}`}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredWorksheetData.map((row, rowIdx) => (
                  <TableRow key={rowIdx} hover>
                    {row.map((cell, cellIdx) => (
                      <TableCell 
                        key={cellIdx}
                        sx={{ 
                          whiteSpace: 'nowrap',
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {cell !== null && cell !== undefined ? String(cell) : ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mx: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Showing {filteredWorksheetData.length} of {worksheetData.length - 1} rows × {worksheetData[0]?.length || 0} columns
            </Typography>
            <Alert severity="info" sx={{ py: 0.5 }}>
              This ground truth will be used to evaluate extraction accuracy
            </Alert>
          </Box>
        </Box>
      )}

      {!loading && !error && worksheetData.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="warning">
            Excel file loaded but contains no data
          </Alert>
        </Box>
      )}
    </Paper>
  );
};

export default ExcelViewer;
